/**
 * Parse a displayed linear equation string back into a LinearEquation.
 *
 * This exists for the presentation cross-check in the validation layer: it
 * re-reads what the student actually sees and confirms it matches the internal
 * math. It is a small recursive-descent parser over plaintext algebra - no
 * eval. It handles integers, fractions (a/b), a single variable, unary minus,
 * +/- between terms, implicit multiplication for coefficients (3x, 3(...)),
 * and single-level parentheses with distribution.
 *
 * Grammar (one side of the equation):
 *   expr   := term (('+' | '-') term)*
 *   term   := [factor] ('(' expr ')' | var | number)
 *   factor := number            // leading coefficient, e.g. 3 in 3(x+2) or 3x
 *
 * The variable letter is auto-detected (first alphabetic char), so x, n, r,
 * a, v all work. Multiple distinct variables cause a parse error.
 */

import { Rational, rat } from "./rational";
import { LinearExpr, LinearEquation } from "./linear";

class Tokenizer {
  private i = 0;
  constructor(private readonly src: string) {}

  private skipSpace(): void {
    while (this.i < this.src.length && this.src[this.i] === " ") this.i++;
  }

  peek(): string | null {
    this.skipSpace();
    return this.i < this.src.length ? (this.src[this.i] as string) : null;
  }

  next(): string | null {
    this.skipSpace();
    return this.i < this.src.length ? (this.src[this.i++] as string) : null;
  }

  /** Read a run of digits (and optional single '/frac') as a Rational. */
  readNumber(): Rational | null {
    this.skipSpace();
    const start = this.i;
    while (this.i < this.src.length && /\d/.test(this.src[this.i] as string)) {
      this.i++;
    }
    if (this.i === start) return null;
    let numStr = this.src.slice(start, this.i);
    // Optional /denominator
    if (this.src[this.i] === "/") {
      this.i++;
      const dStart = this.i;
      while (this.i < this.src.length && /\d/.test(this.src[this.i] as string)) {
        this.i++;
      }
      if (this.i === dStart) return null;
      const den = this.src.slice(dStart, this.i);
      return rat(BigInt(numStr), BigInt(den));
    }
    return rat(BigInt(numStr));
  }

  atEnd(): boolean {
    this.skipSpace();
    return this.i >= this.src.length;
  }
}

class EquationParser {
  private varLetter: string | null = null;

  parse(text: string): LinearEquation {
    const sides = text.split("=");
    if (sides.length !== 2) {
      throw new Error(`expected exactly one '=', got ${sides.length - 1}`);
    }
    const left = this.parseSide(sides[0] as string);
    const right = this.parseSide(sides[1] as string);
    return new LinearEquation(left, right);
  }

  private parseSide(side: string): LinearExpr {
    const t = new Tokenizer(this.detectVar(side));
    const expr = this.parseExpr(t);
    if (!t.atEnd()) {
      throw new Error(`unexpected trailing input in "${side}"`);
    }
    return expr;
  }

  /** Register the variable letter used in this equation (must be consistent). */
  private detectVar(side: string): string {
    for (const ch of side) {
      if (/[a-z]/i.test(ch)) {
        const lower = ch.toLowerCase();
        if (this.varLetter === null) this.varLetter = lower;
        else if (this.varLetter !== lower) {
          throw new Error(
            `multiple variables detected: "${this.varLetter}" and "${lower}"`,
          );
        }
      }
    }
    return side;
  }

  private parseExpr(t: Tokenizer): LinearExpr {
    let sign = 1;
    // Optional leading sign
    if (t.peek() === "+" || t.peek() === "-") {
      sign = t.next() === "-" ? -1 : 1;
    }
    let acc = this.parseTerm(t);
    if (sign === -1) acc = acc.neg();

    for (;;) {
      const op = t.peek();
      if (op === "+" || op === "-") {
        t.next();
        const term = this.parseTerm(t);
        acc = op === "+" ? acc.add(term) : acc.sub(term);
      } else {
        break;
      }
    }
    return acc;
  }

  private parseTerm(t: Tokenizer): LinearExpr {
    // Optional unary sign on the term (tolerates display quirks like "+ -4").
    let termSign = 1;
    const lead = t.peek();
    if (lead === "+" || lead === "-") {
      t.next();
      termSign = lead === "-" ? -1 : 1;
    }

    // Optional numeric coefficient.
    let coeff: Rational | null = null;
    const num = t.readNumber();
    if (num !== null) coeff = num;

    const c = t.peek();
    let result: LinearExpr;
    if (c === "(") {
      t.next();
      const inner = this.parseExpr(t);
      if (t.next() !== ")") throw new Error("missing closing parenthesis");
      result = coeff ? inner.scale(coeff) : inner;
    } else if (c && /[a-z]/i.test(c)) {
      t.next(); // consume variable
      const k = coeff ?? Rational.ONE;
      result = new LinearExpr(k, Rational.ZERO);
    } else if (coeff !== null) {
      // Bare number term.
      result = LinearExpr.constantExpr(coeff);
    } else {
      throw new Error(`unexpected token "${c ?? "<end>"}"`);
    }

    return termSign === -1 ? result.neg() : result;
  }
}

/** Parse a displayed linear equation into the internal model. Throws on error. */
export function parseEquation(text: string): LinearEquation {
  return new EquationParser().parse(text);
}
